import React, { useEffect, useState } from "react";
import DocumentPreviewModal from "../../components/DocumentPreviewModal";
import FileUpload from "../../components/FileUpload";
import { driverApi } from "../../services/driverApi";

const REQUIRED_UPLOADS = [
  { category: "driver_license", label: "Driver's License", hint: "Front and back of your valid Colorado driver's license" },
  { category: "medical_certificate", label: "DOT Medical Certificate", hint: "Current DOT medical examiner's certificate (required by Colorado PUC)" },
  { category: "fingerprint_clearance", label: "FBI Fingerprint Clearance", hint: "Federal fingerprint-based background check clearance letter" },
  { category: "mvr", label: "MVR — Motor Vehicle Record", hint: "3-year driving history from the DMV (required by Colorado PUC Rule 6305)" },
  { category: "vehicle_inspection", label: "Vehicle Inspection Report", hint: "Completed PUC vehicle safety inspection — download the blank form below, fill it out, then upload", download: "/documents/vehicle_inspection_report.pdf" },
];

const DOC_CATEGORIES = [
  "driver_license", "medical_certificate", "background_check",
  "fingerprint_clearance", "mvr", "wc43", "vehicle_registration", "vehicle_inspection",
  "vehicle_maintenance", "airport_badge", "other",
];

const MAINTENANCE_EXAMPLES = [
  "Oil change receipt",
  "Tire rotation / replacement",
  "Brake service record",
  "Air filter / fluid service",
  "Annual tune-up receipt",
  "Any repair invoice",
];

export default function UploadDocuments() {
  const [docs, setDocs] = useState([]);
  const [insuranceCard, setInsuranceCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ documentCategory: "driver_license", label: "", expiryDate: "" });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [preview, setPreview] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [u, ins] = await Promise.all([
        driverApi.listUploads(),
        driverApi.getInsuranceCard().catch(() => null),
      ]);
      setDocs(u.docs || []);
      setInsuranceCard(ins);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("documentCategory", form.documentCategory);
      fd.append("label", form.label || form.documentCategory.replace(/_/g, " "));
      if (form.expiryDate) fd.append("expiryDate", form.expiryDate);
      await driverApi.uploadDoc(fd);
      setFile(null);
      setForm({ documentCategory: "driver_license", label: "", expiryDate: "" });
      setSuccess("Document uploaded successfully.");
      setTimeout(() => setSuccess(""), 3000);
      load();
    } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  }

  async function handleGetLink(docId) {
    try { const res = await driverApi.getDocLink(docId); setPreview(res); }
    catch (err) { setError(err.message); }
  }

  async function loadInsLink() {
    try {
      const res = await driverApi.getInsuranceCard();
      setPreview({
        link: res.link,
        fileName: res.fileName || "Insurance Card",
        mimeType: res.mimeType || "",
      });
    } catch (err) { setError("Could not retrieve insurance card link."); }
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;

  const uploadedCategories = new Set(docs.map((d) => d.documentCategory));

  const isExpired = insuranceCard?.expiryDate && new Date(insuranceCard.expiryDate) < new Date();
  const isExpiringSoon = insuranceCard?.expiryDate && !isExpired &&
    (new Date(insuranceCard.expiryDate) - new Date()) < 30 * 24 * 60 * 60 * 1000;

  return (
    <div>
      {preview && (
        <DocumentPreviewModal
          link={preview.link}
          fileName={preview.fileName}
          mimeType={preview.mimeType}
          onClose={() => setPreview(null)}
        />
      )}
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900">My Documents</h1>
        <p className="text-sm text-gray-500 mt-1">Upload your required compliance documents. All files are stored securely.</p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 mb-4 text-sm">{success}</div>}

      {/* ── Insurance Card from Office ── */}
      <div className={`rounded-xl border p-4 mb-5 ${insuranceCard ? "bg-white border-gray-200" : "bg-gray-50 border-dashed border-gray-300"}`}>
        <div className="flex items-center gap-3 mb-1">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${insuranceCard ? "bg-blue-100" : "bg-gray-100"}`}>
            <svg className={`w-5 h-5 ${insuranceCard ? "text-blue-600" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Insurance Card — Provided by Office</p>
            {insuranceCard ? (
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <p className="text-xs text-gray-500 truncate">{insuranceCard.fileName || "Insurance card on file"}</p>
                {insuranceCard.expiryDate && (
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                    isExpired ? "bg-red-100 text-red-700"
                    : isExpiringSoon ? "bg-yellow-100 text-yellow-700"
                    : "bg-green-100 text-green-700"
                  }`}>
                    {isExpired ? "Expired" : "Expires"} {new Date(insuranceCard.expiryDate).toLocaleDateString()}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 mt-0.5">Not yet uploaded by the office — contact your coordinator.</p>
            )}
          </div>
          {insuranceCard && (
            <button
              onClick={loadInsLink}
              className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold text-blue-600 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              View
            </button>
          )}
        </div>
      </div>

      {/* ── Required uploads checklist ── */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
        <p className="text-sm font-semibold text-blue-800 mb-2">Required Documents Checklist</p>
        <ul className="space-y-1.5">
          {REQUIRED_UPLOADS.map((r) => {
            const done = uploadedCategories.has(r.category);
            return (
              <li key={r.category} className="flex items-start gap-2 text-sm">
                <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${done ? "text-green-500" : "text-blue-300"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={done ? "M5 13l4 4L19 7" : "M12 4v16m8-8H4"} />
                </svg>
                <div>
                  <span className={`font-medium ${done ? "text-green-700" : "text-blue-800"}`}>{r.label}</span>
                  {r.download && !done && (
                    <a href={r.download} download target="_blank" rel="noopener noreferrer"
                      className="ml-2 text-xs text-white bg-blue-500 hover:bg-blue-600 px-1.5 py-0.5 rounded font-medium">
                      Download blank ↓
                    </a>
                  )}
                  <p className="text-blue-600 text-xs mt-0.5">{r.hint}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* ── Maintenance Records ── */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800 mb-1">Vehicle Maintenance Records</p>
            <p className="text-xs text-amber-700 mb-2">
              Upload receipts or service records to document that your vehicle is properly maintained.
              Select <strong>Vehicle Maintenance</strong> as the document type when uploading.
            </p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              {MAINTENANCE_EXAMPLES.map((ex) => (
                <li key={ex} className="text-xs text-amber-700 flex items-center gap-1.5">
                  <svg className="w-3 h-3 flex-shrink-0 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {ex}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Upload form */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-4">Upload a Document</h2>
        <form onSubmit={handleUpload} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
            <select value={form.documentCategory} onChange={(e) => setForm({ ...form, documentCategory: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label (optional)</label>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Driver License — Expires 2027"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (if applicable)</label>
            <input type="date" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <FileUpload onFile={setFile} label="Select your document" />
          <button type="submit" disabled={!file || uploading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg text-sm transition disabled:opacity-50">
            {uploading ? "Uploading…" : "Upload Document"}
          </button>
        </form>
      </div>

      {/* Uploaded docs */}
      {docs.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="font-semibold text-sm text-gray-900">Uploaded Documents ({docs.length})</p>
          </div>
          <ul className="divide-y divide-gray-100">
            {docs.map((doc) => (
              <li key={doc._id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{doc.label}</p>
                  <p className="text-xs text-gray-500">{doc.documentCategory.replace(/_/g, " ")} · {new Date(doc.createdAt).toLocaleDateString()}</p>
                  {doc.expiryDate && <p className="text-xs text-gray-400">Expires: {new Date(doc.expiryDate).toLocaleDateString()}</p>}
                </div>
                <button onClick={() => handleGetLink(doc._id)} className="text-sm text-blue-600 hover:underline font-medium">View</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
